import { createContext, useCallback, useReducer } from "react";

import axios from "../../utils/axios/v1/adminAxios";

const GET_SMARTPARSE_SITES = "GET_SMARTPARSE_SITES";
const SET_SITES_STATUS = "SET_SITES_STATUS";
const SET_SITES_TOTAL = "SET_SITES_TOTAL";
const GET_SMARTPARSE_OVERVIEW = "GET_SMARTPARSE_OVERVIEW";
const SET_BULK_RESULT = "SET_BULK_RESULT";
const SET_ORG_UPDATE_RESULT = "SET_ORG_UPDATE_RESULT";
const SET_ERROR = "SET_ERROR";
const SET_LOADING = "SET_LOADING";

const initialState = {
  sites: null,
  sitesFrom: 0,
  sitesSize: 20,
  sitesTotal: 0,
  overview: null,
  bulkResult: null,
  orgUpdateResult: null,
  loading: false,
  errMsg: null,
};

const SmartParseReducer = (state, action) => {
  switch (action.type) {
    case GET_SMARTPARSE_SITES:
      return {
        ...state,
        sites: action.payload.sites,
      };
    case SET_SITES_STATUS:
      return {
        ...state,
        sitesFrom: action.payload.sitesFrom,
        sitesSize: action.payload.sitesSize,
      };
    case SET_SITES_TOTAL:
      return {
        ...state,
        sitesTotal: action.payload.sitesTotal,
      };
    case GET_SMARTPARSE_OVERVIEW:
      return {
        ...state,
        overview: action.payload.overview,
      };
    case SET_BULK_RESULT:
      return {
        ...state,
        bulkResult: action.payload.bulkResult,
      };
    case SET_ORG_UPDATE_RESULT:
      return {
        ...state,
        orgUpdateResult: action.payload.orgUpdateResult,
      };
    case SET_LOADING:
      return {
        ...state,
        loading: action.payload.loading,
      };
    case SET_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    default:
      return state;
  }
};

const SmartParseContext = createContext(null);

function SmartParseProvider({ children }) {
  const [state, dispatch] = useReducer(SmartParseReducer, initialState);

  const setErr = useCallback((msg) => {
    dispatch({
      type: SET_ERROR,
      payload: {
        errMsg: msg,
      },
    });
  }, []);

  const setLoading = useCallback((loading) => {
    dispatch({
      type: SET_LOADING,
      payload: {
        loading,
      },
    });
  }, []);

  const getSmartParseSites = useCallback(
    async (size = 20, page = 1, filters = {}, init = true) => {
      dispatch({
        type: GET_SMARTPARSE_SITES,
        payload: {
          sites: null,
        },
      });
      dispatch({
        type: SET_SITES_STATUS,
        payload: {
          sitesFrom: (page - 1) * size,
          sitesSize: size,
        },
      });
      if (init) {
        dispatch({
          type: SET_SITES_TOTAL,
          payload: {
            sitesTotal: 0,
          },
        });
      }
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", size);
        if (filters.enabled !== undefined && filters.enabled !== null) {
          params.append("enabled", filters.enabled);
        }
        if (filters.action !== undefined && filters.action !== null) {
          params.append("action", filters.action);
        }
        if (filters.search) {
          params.append("search", filters.search);
        }
        const response = await axios.get(`v1/smartparse/sites?${params.toString()}`);
        dispatch({
          type: GET_SMARTPARSE_SITES,
          payload: {
            sites: response.data.sites || [],
          },
        });
        dispatch({
          type: SET_SITES_TOTAL,
          payload: {
            sitesTotal: response.data.total || 0,
          },
        });
      } catch (err) {
        setErr(err.message);
        dispatch({
          type: GET_SMARTPARSE_SITES,
          payload: {
            sites: [],
          },
        });
        dispatch({
          type: SET_SITES_TOTAL,
          payload: {
            sitesTotal: 0,
          },
        });
      } finally {
        setLoading(false);
      }
    },
    [setErr, setLoading]
  );

  const getSmartParseOverview = useCallback(async () => {
    dispatch({
      type: GET_SMARTPARSE_OVERVIEW,
      payload: {
        overview: null,
      },
    });
    setLoading(true);
    try {
      const response = await axios.get("v1/smartparse/overview");
      dispatch({
        type: GET_SMARTPARSE_OVERVIEW,
        payload: {
          overview: response.data,
        },
      });
      return response.data;
    } catch (err) {
      setErr(err.message);
      dispatch({
        type: GET_SMARTPARSE_OVERVIEW,
        payload: {
          overview: null,
        },
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [setErr, setLoading]);

  const bulkUpdateSmartParse = useCallback(
    async (siteUids, config) => {
      dispatch({
        type: SET_BULK_RESULT,
        payload: {
          bulkResult: null,
        },
      });
      setLoading(true);
      try {
        const response = await axios.patch("v1/smartparse/bulk", {
          site_uids: siteUids,
          config: config,
        });
        dispatch({
          type: SET_BULK_RESULT,
          payload: {
            bulkResult: response.data,
          },
        });
        getSmartParseSites(state.sitesSize, Math.floor(state.sitesFrom / state.sitesSize) + 1);
        return response.data;
      } catch (err) {
        setErr(err.message);
        return { success: false, error: err.message };
      } finally {
        setLoading(false);
      }
    },
    [setErr, setLoading, getSmartParseSites, state.sitesSize, state.sitesFrom]
  );

  const updateSmartParseByOrganisation = useCallback(
    async (orgId, config) => {
      dispatch({
        type: SET_ORG_UPDATE_RESULT,
        payload: {
          orgUpdateResult: null,
        },
      });
      setLoading(true);
      try {
        const response = await axios.patch(`v1/smartparse/organisation/${orgId}`, config);
        dispatch({
          type: SET_ORG_UPDATE_RESULT,
          payload: {
            orgUpdateResult: response.data,
          },
        });
        getSmartParseSites(state.sitesSize, Math.floor(state.sitesFrom / state.sitesSize) + 1);
        return response.data;
      } catch (err) {
        setErr(err.message);
        return { success: false, error: err.message };
      } finally {
        setLoading(false);
      }
    },
    [setErr, setLoading, getSmartParseSites, state.sitesSize, state.sitesFrom]
  );

  const clearBulkResult = useCallback(() => {
    dispatch({
      type: SET_BULK_RESULT,
      payload: {
        bulkResult: null,
      },
    });
  }, []);

  const clearOrgUpdateResult = useCallback(() => {
    dispatch({
      type: SET_ORG_UPDATE_RESULT,
      payload: {
        orgUpdateResult: null,
      },
    });
  }, []);

  return (
    <SmartParseContext.Provider
      value={{
        ...state,
        getSmartParseSites,
        getSmartParseOverview,
        bulkUpdateSmartParse,
        updateSmartParseByOrganisation,
        clearBulkResult,
        clearOrgUpdateResult,
        setErr,
      }}
    >
      {children}
    </SmartParseContext.Provider>
  );
}

export { SmartParseContext, SmartParseProvider };
